import { useState, useEffect, useMemo } from "react";

import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { User, Building2, CreditCard, Bell, Shield, Palette, Moon, Sun, Check, ChevronDown, Plus, Trash2, Settings as SettingsIcon, Percent, MapPin, Search, ExternalLink, Eye, ArrowUp, ArrowDown, LayoutGrid, List, Globe, Crop, Sparkles, Users, UserCheck, Stethoscope } from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { InlinePhotoAdjuster } from "@/components/InlinePhotoAdjuster";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTheme } from "next-themes";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useGetLanguages, useGetProviderTypes, useGetVisitTypes, useGetCredentialTypes, useGetLocalities, useGetCodeTypes, useListFacilityTypes, useListGroupInvitations, useUpdateGroupInvitation, API_BASE } from "@/lib/api-client-react";
import { Checkbox } from "@/components/ui/checkbox";
import { FACILITY_TYPE_LIST } from "@/components/FacilityTypePromptModal";


/* ─── Shared Types & Data ────────────────────────────────────────────────── */
const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];
const SALUTATIONS = ["Dr.", "Mr.", "Mrs.", "Ms.", "Prof."];
const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];
const DEFAULT_SCHEDULES = [
  { dayOfWeek: "Monday", isClosed: false, startTime: "09:00 AM", endTime: "05:00 PM" },
  { dayOfWeek: "Tuesday", isClosed: false, startTime: "09:00 AM", endTime: "05:00 PM" },
  { dayOfWeek: "Wednesday", isClosed: false, startTime: "09:00 AM", endTime: "05:00 PM" },
  { dayOfWeek: "Thursday", isClosed: false, startTime: "09:00 AM", endTime: "05:00 PM" },
  { dayOfWeek: "Friday", isClosed: false, startTime: "09:00 AM", endTime: "05:00 PM" },
  { dayOfWeek: "Saturday", isClosed: true, startTime: "09:00 AM", endTime: "05:00 PM" },
  { dayOfWeek: "Sunday", isClosed: true, startTime: "09:00 AM", endTime: "05:00 PM" },
];

function SingleLocalitySelect({
  value,
  onChange,
  options,
  placeholder = "Select locality..."
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-11 justify-between rounded-xl bg-background border border-border font-normal hover:bg-muted/50 text-sm shadow-2xs transition-all px-3.5 mt-1"
        >
          {selectedOption ? (
            <span className="font-medium text-foreground truncate pr-2">
              {selectedOption.label}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0 rounded-2xl border border-border shadow-xl bg-popover text-popover-foreground overflow-hidden"
        align="start"
        sideOffset={6}
      >
        <div className="p-2 border-b border-border bg-muted/30">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search locality..."
              className="pl-8 h-9 text-xs rounded-xl border-border bg-background"
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto p-1.5 space-y-0.5">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No locality found
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium transition-colors ${isSelected
                    ? "bg-primary/10 text-primary font-semibold"
                    : "hover:bg-muted font-normal"
                    }`}
                >
                  <span className="truncate pr-2">{opt.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CollapsibleSection({
  title,
  subtitle,
  icon: Icon,
  badgeText,
  defaultOpen = true,
  children,
  headerAction,
}: {
  title: string;
  subtitle?: string;
  icon?: any;
  badgeText?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  headerAction?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden transition-all shadow-xs mb-5">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex justify-between p-4 sm:p-5 bg-card hover:bg-muted/30 cursor-pointer select-none transition-colors gap-3",
          headerAction ? "flex-col sm:flex-row sm:items-center" : "items-center"
        )}
      >
        <div className="flex items-center gap-3 pr-2 sm:pr-4 min-w-0">
          {Icon && (
            <div className="p-2.5 bg-primary/10 text-primary rounded-xl shrink-0">
              <Icon className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-bold text-base text-foreground">{title}</h2>
              {badgeText && (
                <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20">
                  {badgeText}
                </Badge>
              )}
            </div>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
        </div>

        <div className={cn("flex items-center gap-2 sm:gap-3 shrink-0", headerAction ? "w-full sm:w-auto justify-between sm:justify-end" : "")} onClick={(e) => e.stopPropagation()}>
          {headerAction}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label={isOpen ? "Collapse section" : "Expand section"}
          >
            <ChevronDown className={cn("w-5 h-5 transition-transform duration-200", isOpen ? "rotate-180" : "rotate-0")} />
          </button>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div className="p-4 sm:p-6 space-y-5 border-t border-border/40">{children}</div>
        </motion.div>
      )}
    </div>
  );
}

type Schedule = { dayOfWeek: string; isClosed: boolean; startTime: string; endTime: string; };
type Location = { isPrimary: boolean; isActive?: boolean; buildingNo: string; address1: string; address2: string; city: string; state: string; zipCode: string; phone: string; officePhone: string; fax: string; email: string; website: string; localityIds?: string[]; schedules: Schedule[]; };
type Credential = { credentialType: string; title: string; institution: string; year: string; };

type FormData = {
  practiceType: "individual" | "group";
  salutation: string; firstName: string; middleName: string; lastName: string;
  displayName: string; gender: string; headline: string; dob: string;
  facilityType?: string; facilityTypeId?: string;
  npiNumber: string; individualNpiNumber?: string; groupNpiNumber?: string; affiliatedName?: string;
  providerTypes: string[]; languages: string[];
  statesLicensed: string[]; credentials: Credential[]; medicareCharge: string;
  synopsis: string; visitTypes: string[]; codeTypes?: string[];
  locations: Location[];
  zocdocUrl: string; webmdUrl: string; healthgradesUrl: string; usnewsUrl: string;
  socialLinks?: { platform: string; url: string }[];
  profilePicture?: string;
  categoryRates?: Record<string, any>;
  isVisibleInFindCare?: boolean;
};


const SOCIAL_PLATFORMS = [
  { value: "Facebook", label: "Facebook" },
  { value: "Instagram", label: "Instagram" },
  { value: "Blog", label: "Blog" },
  { value: "Zocdoc", label: "Zocdoc" },
  { value: "Healthgrades", label: "Healthgrades" },
  { value: "WebMD", label: "WebMD" }
];

function SocialLinksEditor({ control }: { control: any }) {
  const { fields, append, remove } = useFieldArray({ control, name: "socialLinks" });

  return (
    <div className="space-y-3">
      {fields.map((field, index) => (
        <div key={field.id} className="flex flex-col sm:flex-row items-center gap-2.5 bg-muted/20 p-3 rounded-xl border border-border">
          <div className="w-full sm:w-44">
            <Controller
              control={control}
              name={`socialLinks.${index}.platform`}
              render={({ field: pField }) => (
                <Select value={pField.value || "Zocdoc"} onValueChange={pField.onChange}>
                  <SelectTrigger className="h-10 text-xs font-semibold rounded-xl bg-background">
                    <SelectValue placeholder="Select Platform" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {SOCIAL_PLATFORMS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="flex-1 w-full">
            <Controller
              control={control}
              name={`socialLinks.${index}.url`}
              render={({ field: uField }) => (
                <Input
                  {...uField}
                  placeholder={`https://...`}
                  className="h-10 text-xs rounded-xl"
                />
              )}
            />
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => remove(index)}
            className="h-10 w-10 text-destructive hover:bg-destructive/10 rounded-xl shrink-0"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}

      {fields.length === 0 && (
        <p className="text-xs text-muted-foreground italic mb-2">No external profile or social links added yet. Click below to add one.</p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => append({ platform: "Zocdoc", url: "" })}
        className="rounded-xl text-xs gap-1.5 border-dashed"
      >
        <Plus className="w-3.5 h-3.5" /> Add Profile / Social Link
      </Button>
    </div>
  );
}

/* ─── MultiSelect Component ──────────────────────────────────────────────── */
function MultiSelect({ options = [], allOptions = [], value = [], onChange, placeholder, maxHeight = 260 }: any) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = (options || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o, name: o, id: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      const name = String(o.name ?? o.label ?? o.value ?? "");
      const id = String(o.id ?? o.value ?? "");
      return { value: val, label: lbl, name, id };
    }
    return { value: String(o ?? ""), label: String(o ?? ""), name: String(o ?? ""), id: String(o ?? "") };
  });

  const normalizedAllOptions = (allOptions || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o, name: o, id: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      const name = String(o.name ?? o.label ?? o.value ?? "");
      const id = String(o.id ?? o.value ?? "");
      return { value: val, label: lbl, name, id };
    }
    return { value: String(o ?? ""), label: String(o ?? ""), name: String(o ?? ""), id: String(o ?? "") };
  });

  const filtered = normalizedOptions.filter(o =>
    o.label.toLowerCase().includes((search || "").toLowerCase())
  );

  const safeValue = Array.isArray(value) ? value : [];

  const isOptionSelected = (opt: any) => {
    const optValStr = String(opt.value ?? opt.id ?? opt.name ?? "").toLowerCase();
    const optLblStr = String(opt.label ?? opt.name ?? opt.value ?? "").toLowerCase();
    const optIdStr = String(opt.id ?? opt.value ?? "").toLowerCase();

    return safeValue.some((v: any) => {
      if (!v) return false;
      const vStr = String(typeof v === "object" ? (v.value ?? v.id ?? v.name ?? "") : v).toLowerCase();
      return vStr === optValStr || vStr === optIdStr || vStr === optLblStr;
    });
  };

  const toggle = (opt: any) => {
    const optObj = typeof opt === "string" ? { value: opt, label: opt, name: opt, id: opt } : opt;
    const optValStr = String(optObj.value ?? optObj.id ?? optObj.name ?? "");
    const optValLower = optValStr.toLowerCase();
    const optLblLower = String(optObj.label ?? optObj.name ?? optObj.value ?? "").toLowerCase();
    const optIdLower = String(optObj.id ?? optObj.value ?? "").toLowerCase();

    const isSelected = isOptionSelected(optObj);

    if (isSelected) {
      onChange(
        safeValue.filter((v: any) => {
          if (!v) return false;
          const vStr = String(typeof v === "object" ? (v.value ?? v.id ?? v.name ?? "") : v).toLowerCase();
          const matches = vStr === optValLower || vStr === optIdLower || vStr === optLblLower;
          return !matches;
        })
      );
    } else {
      onChange([...safeValue, optValStr]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="w-full min-h-[40px] px-3 py-2 border border-input rounded-md bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors">
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            (() => {
              const renderedLabels = new Set<string>();
              return safeValue.map((v: any) => {
                const strV = String(v);
                const found = normalizedOptions.find((o: any) =>
                  o.value === strV ||
                  o.value.toLowerCase() === strV.toLowerCase() ||
                  o.label === strV ||
                  o.label.toLowerCase() === strV.toLowerCase() ||
                  (o.name && o.name.toLowerCase() === strV.toLowerCase()) ||
                  (o.id && o.id.toLowerCase() === strV.toLowerCase())
                ) || normalizedAllOptions.find((o: any) =>
                  o.value === strV ||
                  o.value.toLowerCase() === strV.toLowerCase() ||
                  o.label === strV ||
                  o.label.toLowerCase() === strV.toLowerCase() ||
                  (o.name && o.name.toLowerCase() === strV.toLowerCase()) ||
                  (o.id && o.id.toLowerCase() === strV.toLowerCase())
                );
                const label = found ? found.label : strV;
                if (renderedLabels.has(label.toLowerCase())) return null;
                renderedLabels.add(label.toLowerCase());

                return (
                  <Badge key={strV} variant="secondary" className="text-xs rounded-full px-2.5 py-0.5 bg-primary/10 text-primary border border-primary/20 flex items-center gap-1 font-medium">
                    <span>{label}</span>
                    <span className="ml-0.5 cursor-pointer hover:text-destructive font-bold text-xs" onClick={e => { e.stopPropagation(); toggle(found || { value: strV, label: strV }); }}>×</span>
                  </Badge>
                );
              }).filter(Boolean);
            })()
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-2 border-b">
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm" autoFocus />
        </div>
        <div className="overflow-y-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No results</p>
          ) : filtered.map((opt: any) => {
            const checked = isOptionSelected(opt);
            return (
              <button key={opt.value} type="button" onClick={() => toggle(opt)} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left">
                <div className={cn("w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors", checked ? "bg-primary border-primary" : "border-input")}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                {opt.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}


function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return <Label className="text-sm font-medium text-foreground">{children}{required && <span className="text-destructive ml-0.5">*</span>}</Label>;
}

export default function ProviderSettings() {
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(true);
  const [patientMessages, setPatientMessages] = useState(true);

  const [user, setUser] = useState<any>(null);

  const { data: languagesData = [] } = useGetLanguages();
  const { data: providerTypesData = [] } = useGetProviderTypes();
  const { data: visitTypesData = [] } = useGetVisitTypes();
  const { data: credentialTypesData = [] } = useGetCredentialTypes();
  const { data: codeTypesData = [] } = useGetCodeTypes();
  const { data: masterLocalitiesRes = [] } = useGetLocalities();

  const activeCodeTypes = useMemo(() => {
    if (codeTypesData && codeTypesData.length > 0) {
      return codeTypesData
        .filter((ct: any) => ct && ct.isActive !== false)
        .map((ct: any, idx: number) => {
          const name = typeof ct === "object" && ct !== null ? (ct.name || ct.code || `CodeType-${idx}`) : String(ct || `CodeType-${idx}`);
          const code = typeof ct === "object" && ct !== null ? (ct.code || ct.name || name) : name;
          const id = typeof ct === "object" && ct !== null && ct.id != null ? String(ct.id) : code;
          return { id, name, code };
        });
    }
    return [
      { id: "CPT", name: "CPT", code: "CPT" },
      { id: "PFS", name: "PFS", code: "PFS" },
      { id: "CLFS", name: "CLFS", code: "CLFS" },
      { id: "HCPCS", name: "HCPCS", code: "HCPCS" },
      { id: "Category II", name: "Category II", code: "Category II" },
      { id: "Category III", name: "Category III", code: "Category III" },
    ];
  }, [codeTypesData]);

  const CODE_TYPE_OPTIONS = useMemo(() => {
    return activeCodeTypes.map((ct: any) => ({ value: String(ct.id), label: ct.name, name: ct.name }));
  }, [activeCodeTypes]);



  const masterLocalitiesData: any[] = Array.isArray(masterLocalitiesRes) ? masterLocalitiesRes : ((masterLocalitiesRes as any)?.data || []);

  const MASTER_LOCALITIES = masterLocalitiesData.map((l: any) => ({
    value: String(l.id || l.value || ""),
    label: `${l.name} (${l.state}${l.city ? ' - ' + l.city : ''})`
  }));

  const getLocalityLabel = (locId: string) => {
    const found = masterLocalitiesData.find((l: any) => String(l.id || l.value || "") === String(locId) || l.name === locId);
    return found ? `${found.name} (${found.state}${found.city ? ' - ' + found.city : ''})` : locId;
  };

  const getProviderTypeName = (typeId: string) => {
    const found = providerTypesData.find((p: any) => String(p.id || p.value || "") === String(typeId) || p.name === typeId);
    return found ? found.name : typeId;
  };

  const getRateForLocality = (rates: any, locId: string, typeName: string, typeId: string, locItem: any, idx: number) => {
    if (!rates || typeof rates !== "object") return 100;

    // 1. Direct lookup by locId
    if (rates[locId] && typeof rates[locId] === "object") {
      const val = rates[locId][typeName] ?? rates[locId][typeId];
      if (val !== undefined && val !== null) return val;
    }

    // 2. Lookup by locality label (e.g. "NYC SUBURBS/LONG ISLAND (NY)")
    const locLabel = getLocalityLabel(locId);
    if (locLabel && rates[locLabel] && typeof rates[locLabel] === "object") {
      const val = rates[locLabel][typeName] ?? rates[locLabel][typeId];
      if (val !== undefined && val !== null) return val;
    }

    // 3. Lookup by master locality name or locality number
    const foundLoc = masterLocalitiesData.find((l: any) => l.id.toString() === locId || l.name === locId);
    if (foundLoc?.name && rates[foundLoc.name] && typeof rates[foundLoc.name] === "object") {
      const val = rates[foundLoc.name][typeName] ?? rates[foundLoc.name][typeId];
      if (val !== undefined && val !== null) return val;
    }
    if (foundLoc?.localityNumber && rates[foundLoc.localityNumber] && typeof rates[foundLoc.localityNumber] === "object") {
      const val = rates[foundLoc.localityNumber][typeName] ?? rates[foundLoc.localityNumber][typeId];
      if (val !== undefined && val !== null) return val;
    }

    // 4. Fallback to flat rates ONLY if NO locality nested objects exist in rates
    const hasNestedLocalityObjects = Object.values(rates).some((v) => typeof v === "object" && v !== null);
    if (!hasNestedLocalityObjects) {
      if (rates[typeName] !== undefined && rates[typeName] !== null && typeof rates[typeName] !== "object") {
        return rates[typeName];
      }
      if (rates[typeId] !== undefined && rates[typeId] !== null && typeof rates[typeId] !== "object") {
        return rates[typeId];
      }
    }

    return 100;
  };


  const [categoryRates, setCategoryRates] = useState<Record<string, any>>({});
  const [locationsViewMode, setLocationsViewMode] = useState<'list' | 'grid'>('list');
  const [selectedRateLocationIndex, setSelectedRateLocationIndex] = useState<number>(-1);

  const { data: teamInvitations, isLoading: teamInvitationsLoading, refetch: refetchTeamInvitations } = useListGroupInvitations();
  const updateInvitationMutation = useUpdateGroupInvitation();

  const getLocationKeys = (loc: any, idx: number): string[] => {
    const keys: string[] = [];
    if (loc?.id) keys.push(String(loc.id).trim());
    keys.push(`loc_${idx}`);
    keys.push(`location-${idx}`);

    const street = [loc?.buildingNo, loc?.address1 || loc?.street1].filter(Boolean).join(" ");
    if (street && street.trim()) keys.push(street.trim());

    const addr1 = loc?.address1 || loc?.street1;
    if (addr1 && String(addr1).trim()) keys.push(String(addr1).trim());

    if (loc?.city && loc?.state) keys.push(`${loc.city}, ${loc.state}`);
    return keys;
  };

  const getEffectiveRateForCodeType = (codeTypeName: string, codeTypeId: string): { rate: number; isCustom: boolean } => {
    const globalRate = categoryRates[codeTypeName] ?? categoryRates[codeTypeId] ?? 100;
    if (selectedRateLocationIndex === -1) {
      return { rate: Number(globalRate), isCustom: false };
    }

    const locFieldsList = form.getValues("locations") || [];
    const loc = locFieldsList[selectedRateLocationIndex];
    if (!loc) return { rate: Number(globalRate), isCustom: false };

    const keys = getLocationKeys(loc, selectedRateLocationIndex);
    for (const k of keys) {
      if (categoryRates[k] && typeof categoryRates[k] === "object") {
        const val = categoryRates[k][codeTypeName] ?? categoryRates[k][codeTypeId];
        if (val !== undefined && val !== null) {
          return { rate: Number(val), isCustom: true };
        }
      }
    }
    return { rate: Number(globalRate), isCustom: false };
  };

  const handleUpdateCodeTypeRate = (codeTypeName: string, codeTypeId: string, newRate: number) => {
    setCategoryRates(prev => {
      const updated = { ...prev };
      if (selectedRateLocationIndex === -1) {
        updated[codeTypeName] = newRate;
        if (codeTypeId && isUUID(codeTypeId)) {
          delete updated[codeTypeId];
        }
      } else {
        const locFieldsList = form.getValues("locations") || [];
        const loc = locFieldsList[selectedRateLocationIndex];
        if (loc) {
          const keys = getLocationKeys(loc, selectedRateLocationIndex);
          let existingSubObj: Record<string, any> = {};
          for (const k of keys) {
            if (updated[k] && typeof updated[k] === "object") {
              existingSubObj = { ...updated[k] };
              break;
            }
          }
          existingSubObj[codeTypeName] = newRate;
          keys.forEach(k => {
            updated[k] = { ...(updated[k] || {}), ...existingSubObj };
          });
        }
      }
      form.setValue("categoryRates" as any, updated, { shouldDirty: true });
      return updated;
    });
  };

  const handleResetLocationRatesToDefault = (locIdx: number) => {
    const locFieldsList = form.getValues("locations") || [];
    const loc = locFieldsList[locIdx];
    if (!loc) return;
    const keys = getLocationKeys(loc, locIdx);
    setCategoryRates(prev => {
      const updated = { ...prev };
      keys.forEach(k => {
        delete updated[k];
      });
      form.setValue("categoryRates" as any, updated, { shouldDirty: true });
      return updated;
    });
  };

  const [isAddCodeTypeModalOpen, setIsAddCodeTypeModalOpen] = useState(false);
  const [newCodeTypeName, setNewCodeTypeName] = useState("");
  const [customCodeTypeName, setCustomCodeTypeName] = useState("");
  const [newCodeTypeRate, setNewCodeTypeRate] = useState<number | "">(100);

  const handleAddBillingCodeType = () => {
    const targetName = newCodeTypeName === "OTHER_CUSTOM" ? customCodeTypeName.trim() : newCodeTypeName.trim();
    if (!targetName) return;

    const rateVal = typeof newCodeTypeRate === "number" && !isNaN(newCodeTypeRate) ? newCodeTypeRate : 100;

    setCategoryRates(prev => {
      const updated = {
        ...prev,
        [targetName]: rateVal
      };
      form.setValue("categoryRates" as any, updated, { shouldDirty: true });
      return updated;
    });

    const currentCodeTypes = form.getValues("codeTypes") || [];
    const existsInCodeTypes = currentCodeTypes.some((ct: any) => {
      const s = typeof ct === "object" && ct !== null ? String(ct.name || ct.id || ct.code || "") : String(ct);
      return s.toLowerCase() === targetName.toLowerCase();
    });

    if (!existsInCodeTypes) {
      const updatedCodeTypes = [...currentCodeTypes, targetName];
      form.setValue("codeTypes" as any, updatedCodeTypes, { shouldDirty: true });
    }

    setNewCodeTypeName("");
    setCustomCodeTypeName("");
    setNewCodeTypeRate(100);
    setIsAddCodeTypeModalOpen(false);
    toast({ title: "Billing Code Type Added", description: `Added "${targetName}" with rate ${rateVal}%.` });
  };

  const handleRemoveBillingCodeType = (typeName: string, typeId: string) => {
    setCategoryRates(prev => {
      const updated = { ...prev };
      delete updated[typeName];
      delete updated[typeId];
      form.setValue("categoryRates" as any, updated, { shouldDirty: true });
      return updated;
    });

    const currentCodeTypes = form.getValues("codeTypes") || [];
    const updatedCodeTypes = currentCodeTypes.filter((ct: any) => {
      const s = typeof ct === "object" && ct !== null ? String(ct.name || ct.id || ct.code || "") : String(ct);
      return s.toLowerCase() !== typeName.toLowerCase() && s.toLowerCase() !== typeId.toLowerCase();
    });
    form.setValue("codeTypes" as any, updatedCodeTypes, { shouldDirty: true });

    toast({ title: "Removed", description: `Removed "${typeName}" billing code type.` });
  };

  const LANGUAGES = languagesData.map((l: any) => ({ value: String(l.id || l.value || ""), label: l.name }));
  const PROVIDER_TYPES = providerTypesData.map((p: any) => ({ value: String(p.id || p.value || ""), label: p.name }));
  const VISIT_TYPES_OPTIONS = visitTypesData.map((v: any) => ({ value: String(v.id || v.value || ""), label: v.name }));
  const CREDENTIAL_TYPES = credentialTypesData.map((c: any) => ({ value: String(c.id || c.value || ""), label: c.name }));
  const US_STATES_OPTIONS = US_STATES.map(s => ({ value: s, label: s }));


  const TIME_OPTIONS = Array.from({ length: 48 }).map((_, i) => {
    const h = Math.floor(i / 2);
    const m = i % 2 === 0 ? '00' : '30';
    const ampm = h < 12 ? 'AM' : 'PM';
    const hour = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${hour.toString().padStart(2, '0')}:${m} ${ampm}`;
  });

  const form = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      practiceType: "group",
      salutation: "", firstName: "", middleName: "", lastName: "",
      displayName: "", gender: "", headline: "", dob: "",
      facilityType: "", facilityTypeId: "",
      npiNumber: "", individualNpiNumber: "", groupNpiNumber: "", providerTypes: [], languages: [], statesLicensed: [],
      credentials: [], medicareCharge: "",
      synopsis: "", visitTypes: [],
      locations: [{
        isPrimary: true, buildingNo: "", address1: "", address2: "", city: "", state: "",
        zipCode: "", phone: "", officePhone: "", fax: "", email: "", website: "", schedules: [...DEFAULT_SCHEDULES],
      }],
      zocdocUrl: "", webmdUrl: "", healthgradesUrl: "", usnewsUrl: "",
      socialLinks: [],
      profilePicture: "",
    },
  });

  const { register, control, handleSubmit, reset, watch, setValue, formState: { isDirty } } = form;
  const { fields: credFields, append: addCred, remove: removeCred } = useFieldArray({ control, name: "credentials" });
  const { fields: locFields, append: addLoc, remove: removeLoc, move: moveLoc } = useFieldArray({ control, name: "locations" });

  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(str || ""));

  const selectedCodeTypeIds = watch("codeTypes") || [];
  const displayedCodeTypes = useMemo(() => {
    const items: any[] = [];
    const seenKeys = new Set<string>();

    const markSeen = (id: string, name: string, code?: string) => {
      if (id) seenKeys.add(String(id).toLowerCase());
      if (name) seenKeys.add(String(name).toLowerCase());
      if (code) seenKeys.add(String(code).toLowerCase());
    };

    const isSeen = (id: string, name: string, code?: string) => {
      return (
        (id && seenKeys.has(String(id).toLowerCase())) ||
        (name && seenKeys.has(String(name).toLowerCase())) ||
        (code && seenKeys.has(String(code).toLowerCase()))
      );
    };

    const addItem = (id: string, name: string, code?: string) => {
      const match = activeCodeTypes.find((ct: any) =>
        (id && ct.id.toLowerCase() === String(id).toLowerCase()) ||
        (name && ct.name.toLowerCase() === String(name).toLowerCase()) ||
        (code && ct.code.toLowerCase() === String(code).toLowerCase()) ||
        (id && ct.name.toLowerCase() === String(id).toLowerCase()) ||
        (name && ct.id.toLowerCase() === String(name).toLowerCase())
      );

      const finalId = match?.id || id || name;
      const finalName = match?.name || (isUUID(name) ? (match?.name || name) : name);
      const finalCode = match?.code || code || finalName;

      if (isUUID(finalName) && !match) {
        return;
      }

      if (isSeen(finalId, finalName, finalCode)) {
        return;
      }

      markSeen(finalId, finalName, finalCode);
      if (match) {
        markSeen(match.id, match.name, match.code);
      }

      items.push({ id: finalId, name: finalName, code: finalCode });
    };

    // 1. Add selected code types from watch("codeTypes")
    if (selectedCodeTypeIds && selectedCodeTypeIds.length > 0) {
      selectedCodeTypeIds.forEach((sId: any) => {
        if (!sId) return;
        const s = typeof sId === "object" ? String(sId.name || sId.id || sId.value || "") : String(sId);
        if (!s) return;
        const match = activeCodeTypes.find((ct: any) => ct.name.toLowerCase() === s.toLowerCase() || ct.id.toLowerCase() === s.toLowerCase());
        if (match) {
          addItem(match.id, match.name, match.code);
        } else if (!isUUID(s)) {
          addItem(s, s, s);
        }
      });
    }

    // 2. Add code types present in categoryRates keys
    if (categoryRates && typeof categoryRates === "object") {
      Object.keys(categoryRates).forEach(k => {
        if (typeof categoryRates[k] !== "object" && categoryRates[k] !== undefined && categoryRates[k] !== null && !k.startsWith("loc_") && k !== "default") {
          const match = activeCodeTypes.find((ct: any) => ct.id.toLowerCase() === k.toLowerCase() || ct.name.toLowerCase() === k.toLowerCase());
          if (match) {
            addItem(match.id, match.name, match.code);
          } else if (!isUUID(k)) {
            addItem(k, k, k);
          }
        }
      });
    }

    // 3. Fallback to activeCodeTypes if nothing selected or in categoryRates
    if (items.length === 0 && activeCodeTypes && activeCodeTypes.length > 0) {
      activeCodeTypes.forEach((ct: any) => addItem(ct.id, ct.name, ct.code));
    }

    return items;
  }, [activeCodeTypes, selectedCodeTypeIds, categoryRates]);


  const [pendingNav, setPendingNav] = useState<string | null>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!isDirty) return;
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (link && link.href) {
        if (!link.href.includes("/provider/settings")) {
          e.preventDefault();
          e.stopPropagation();
          setPendingNav(link.getAttribute("href") || "");
        }
      }
    };
    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, [isDirty]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const json = await res.json();

        if (json.success) {
          const parsed = json.data;
          setUser(parsed);
          localStorage.setItem("user", JSON.stringify(parsed)); // Keep local storage in sync with fresh data
          const rawRates = parsed.provider?.categoryRates || {};
          const cleanedRates: Record<string, any> = {};
          if (rawRates && typeof rawRates === "object") {
            Object.entries(rawRates).forEach(([k, v]) => {
              if (isUUID(k)) {
                const match = activeCodeTypes.find((ct: any) => ct.id.toLowerCase() === k.toLowerCase());
                if (match) {
                  cleanedRates[match.name] = v;
                }
              } else {
                cleanedRates[k] = v;
              }
            });
          }
          setCategoryRates(cleanedRates);

          // Pre-fill form with user data
          reset({
            practiceType: parsed.provider?.practiceType || "group",
            salutation: parsed.provider?.salutation || "",
            facilityType: parsed.facilityType || parsed.provider?.facilityType || "",
            facilityTypeId: parsed.facilityTypeId || parsed.provider?.facilityTypeId || "",
            firstName: parsed.firstName || "",
            middleName: parsed.middleName || "",
            lastName: parsed.lastName || "",
            displayName: parsed.displayName || "",
            gender: parsed.gender || "",
            dob: parsed.dob || "",
            headline: parsed.provider?.headline || "",
            npiNumber: parsed.provider?.npiNumber || "",
            individualNpiNumber: parsed.provider?.individualNpiNumber || "",
            groupNpiNumber: parsed.provider?.groupNpiNumber || "",
            affiliatedName: parsed.provider?.affiliatedName || parsed.affiliatedName || "",
            providerTypes: parsed.provider?.providerTypes?.map((t: any) => String(t.id || t.value || t)) || [],
            languages: parsed.languages?.map((l: any) => String(l.id || l.value || l)) || [],
            statesLicensed: parsed.provider?.statesLicensed || [],
            credentials: parsed.provider?.providerCredentials?.map((c: any) => ({
              credentialType: c.credentialTypeId ? String(c.credentialTypeId) : undefined,
              title: c.title || "",
              institution: c.institution || "",
              year: c.year
            })) || [],
            socialLinks: (() => {
              if (Array.isArray(parsed.provider?.socialLinks) && parsed.provider.socialLinks.length > 0) {
                return parsed.provider.socialLinks;
              }
              const legacy: { platform: string; url: string }[] = [];
              if (parsed.provider?.zocdocUrl) legacy.push({ platform: "Zocdoc", url: parsed.provider.zocdocUrl });
              if (parsed.provider?.webmdUrl) legacy.push({ platform: "WebMD", url: parsed.provider.webmdUrl });
              if (parsed.provider?.healthgradesUrl) legacy.push({ platform: "Healthgrades", url: parsed.provider.healthgradesUrl });
              if (parsed.provider?.usnewsUrl) legacy.push({ platform: "US News", url: parsed.provider.usnewsUrl });
              return legacy;
            })(),
            synopsis: parsed.provider?.synopsis || "",
            visitTypes: parsed.provider?.visitTypes?.map((v: any) => String(v.id || v.value || v)) || [],
            codeTypes: parsed.provider?.codeTypes?.map((ct: any) => {
              if (!ct) return "";
              if (typeof ct === "string") return ct;
              return String(ct.id || ct.code || ct.name || "");
            }).filter(Boolean) || [],

            locations: parsed.addresses?.length ? parsed.addresses.map((a: any) => {
              let locLocalityIds = a.localityIds || [];
              if (!Array.isArray(locLocalityIds) || locLocalityIds.length === 0) {
                if (a.localityId) {
                  locLocalityIds = [a.localityId];
                } else if (parsed.localities && parsed.localities.length > 0) {
                  const userLocs = parsed.localities || [];
                  const stateLocs = a.state
                    ? userLocs.filter((l: any) => l.state === a.state)
                    : userLocs;
                  locLocalityIds = (stateLocs.length > 0 ? stateLocs : userLocs).map((l: any) => String(l.id || l.value || l));
                }
              }

              return {
                ...a,
                address1: a.street1 || "",
                address2: a.street2 || "",
                localityIds: locLocalityIds,
                schedules: DEFAULT_SCHEDULES.map(def => {
                  const found = a.schedules?.find((s: any) => s.dayOfWeek === def.dayOfWeek);
                  return found ? found : def;
                })
              };
            }) : [{
              isPrimary: true,
              buildingNo: "",
              address1: "",
              address2: "",
              city: "",
              state: parsed.localities?.[0]?.state || "",
              zipCode: "",
              phone: parsed.phone || "",
              officePhone: "",
              fax: "",
              email: parsed.email || "",
              website: "",
              localityIds: parsed.localities?.map((l: any) => String(l.id || l.value || l)) || [],
              schedules: [...DEFAULT_SCHEDULES]
            }],

            zocdocUrl: parsed.provider?.zocdocUrl || "",
            webmdUrl: parsed.provider?.webmdUrl || "",
            healthgradesUrl: parsed.provider?.healthgradesUrl || "",
            usnewsUrl: parsed.provider?.usnewsUrl || "",
            profilePicture: parsed.profilePicture || "",
            categoryRates: cleanedRates,
            isVisibleInFindCare: parsed.provider?.isVisibleInFindCare !== undefined ? parsed.provider.isVisibleInFindCare : true,
          });
        }
      } catch (e) {
        console.error("Failed to fetch user settings:", e);
      }
    };
    fetchUserData();
  }, [reset]);

  // Auto-clear localityIds if state changes for a location
  useEffect(() => {
    const locs = watch("locations");
    if (!locs || !Array.isArray(locs)) return;

    locs.forEach((locItem: any, idx: number) => {
      const locState = locItem?.state;
      const currentLocalityIds = locItem?.localityIds;
      if (locState && Array.isArray(currentLocalityIds) && currentLocalityIds.length > 0) {
        const validForState = currentLocalityIds.filter((locId: string) => {
          const found = masterLocalitiesData.find((l: any) => l.id.toString() === locId);
          return found ? found.state === locState : true;
        });
        if (validForState.length !== currentLocalityIds.length) {
          setValue(`locations.${idx}.localityIds` as const, validForState, { shouldDirty: true });
        }
      }
    });
  }, [watch("locations"), masterLocalitiesData, setValue]);

  const handleViewPublicProfile = () => {
    const targetId = user?.id || user?.provider?.id || user?.providerId;
    if (!targetId) {
      toast({ title: "Profile Not Ready", description: "Your provider profile ID could not be loaded.", variant: "destructive" });
      return;
    }
    const nameStr = (user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`).trim();
    const nameSlug = nameStr ? encodeURIComponent(nameStr.toLowerCase().replace(/\s+/g, '-')) : "";
    const targetUrl = nameSlug ? `/member/providers/${nameSlug}/${targetId}?preview=true` : `/member/providers/${targetId}?preview=true`;
    window.open(targetUrl, "_blank");
  };

  const displayName = watch("displayName") || (watch("firstName") ? `${watch("firstName")} ${watch("lastName")}` : "");
  const fallbackChars = displayName ? displayName.substring(0, 2).toUpperCase() : "DR";

  const [isPhotoAdjusterOpen, setIsPhotoAdjusterOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string>("");
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        setTempImageSrc(reader.result as string);
        setIsPhotoAdjusterOpen(true);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleAdjustExistingPhoto = () => {
    const currentPic = watch("profilePicture");
    if (!currentPic) return;
    const fullUrl = currentPic.startsWith("http") ? currentPic : `${API_BASE.replace(/\/v1|\/api/, '')}${currentPic.startsWith('/') ? '' : '/'}${currentPic}`;
    setTempImageSrc(fullUrl);
    setIsPhotoAdjusterOpen(true);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setIsPhotoUploading(true);
    const formData = new FormData();
    formData.append("avatar", croppedBlob, "profile-avatar.jpg");

    try {
      const res = await fetch(`${API_BASE}/upload/avatar`, {
        method: "POST",
        body: formData,
      });
      const json = await res.json();

      if (json.success) {
        toast({ title: "Photo Updated", description: "Centered profile photo saved. Remember to save all changes." });
        form.setValue("profilePicture", json.url, { shouldDirty: true });
        setIsPhotoAdjusterOpen(false);
      } else {
        throw new Error(json.error || "Upload failed");
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to upload photo", variant: "destructive" });
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const handleSave = async (data: FormData) => {
    const payload = {
      ...data,
      categoryRates,
    };
    console.log("Saving settings...", payload);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/providers/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (json.success) {
        toast({ title: "Settings saved", description: "Your provider settings have been updated." });
        setUser(json.data); // Update local user state
        localStorage.setItem("user", JSON.stringify(json.data)); // Update localStorage to sync across tabs/components
        reset(data); // Reset form state so isDirty becomes false
      } else {
        throw new Error(json.error || "Failed to update");
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to save settings", variant: "destructive" });
    }
  };

  const onFormError = (errors: any) => {
    console.error("Form validation errors:", errors);
    const keys = Object.keys(errors);
    toast({
      title: "Validation Error",
      description: `Please check the form fields: ${keys.join(", ")}`,
      variant: "destructive"
    });
  };

  return (
    <ProviderLayout>
      <div className="w-full pt-4 sm:pt-6 lg:pt-8 space-y-5 sm:space-y-6">
        <form onSubmit={handleSubmit(handleSave, onFormError)}>
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 bg-card p-4 sm:p-5 rounded-2xl border border-border shadow-xs mb-6">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0">
                <SettingsIcon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">Provider Settings</h1>
                <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">Manage your practice and professional profile.</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={handleViewPublicProfile}
                className="gap-2 rounded-xl h-9 sm:h-10 px-4 font-semibold text-xs sm:text-sm border-primary/30 text-primary hover:bg-primary/10 shadow-2xs"
                title="View your public profile in Find Care as patients see it"
              >
                <ExternalLink className="w-3.5 h-3.5 text-primary" />
                View Public Profile
              </Button>
              <Button
                type="submit"
                className="h-9 sm:h-10 px-5 font-semibold text-xs sm:text-sm shadow-2xs bg-gradient-to-r from-primary to-secondary text-white border-0 rounded-xl disabled:opacity-50"
                disabled={!isDirty}
              >
                Save All Changes
              </Button>
            </div>
          </div>

          <Tabs defaultValue="provider">
            <TabsList className="bg-muted/80 backdrop-blur-xs p-1.5 rounded-2xl border border-border flex flex-wrap h-auto gap-1.5 mb-6 w-fit">
              {[
                { id: "provider", icon: User, label: "Provider" },
                { id: "locations", icon: Building2, label: "Locations & Hours" },
                { id: "rates", icon: Percent, label: "Fee Schedule Rates" },
                // { id: "notifications", icon: Bell, label: "Notifications" },
                { id: "appearance", icon: Palette, label: "Appearance" },
              ].map(s => (
                <TabsTrigger
                  key={s.id}
                  value={s.id}
                  className="rounded-xl px-4 py-2 gap-2 text-xs sm:text-sm font-semibold transition-all data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-xs"
                  data-testid={`tab-${s.id}`}
                >
                  <s.icon className="w-4 h-4 shrink-0" />
                  <span>{s.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="provider">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                <CollapsibleSection
                  title="Personal Information"
                  subtitle="Profile photo, Find Care visibility, name, gender, and language details"
                  icon={User}
                >
                  {/* Top Row: Profile Photo + Actions & Find Care Visibility Switch */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 mb-2 border-b border-border">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-14 h-14 ring-2 ring-primary/20 shadow-sm shrink-0">
                        {watch("profilePicture") ? (
                          <img src={`${API_BASE.replace(/\/v1|\/api/, '')}${watch("profilePicture")}`} alt="Profile" className="object-cover w-full h-full" />
                        ) : (
                          <AvatarFallback className="bg-secondary/10 text-secondary text-lg font-bold">{fallbackChars}</AvatarFallback>
                        )}
                      </Avatar>
                      <div>
                        <p className="font-semibold text-base leading-tight">{displayName || "Provider"}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{watch("providerTypes")?.[0] || "Provider"}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="file"
                            id="photo-upload"
                            className="hidden"
                            accept="image/*"
                            onChange={handlePhotoSelect}
                          />
                          <Label
                            htmlFor="photo-upload"
                            className="inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors border border-input bg-background shadow-2xs hover:bg-accent hover:text-accent-foreground h-7 rounded-lg px-2.5 text-xs cursor-pointer"
                          >
                            Change Photo
                          </Label>
                          {watch("profilePicture") && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleAdjustExistingPhoto}
                              className="h-7 rounded-lg px-2.5 text-xs gap-1 border-primary/30 text-primary hover:bg-primary/5"
                            >
                              <Crop className="w-3 h-3" /> Adjust Position
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Find Care Search Visibility Switch */}
                    <div className="flex items-center justify-between md:justify-end gap-3 bg-muted/30 p-2.5 rounded-xl border border-border shrink-0">
                      <div className="space-y-0.5">
                        <div className="font-semibold text-xs flex items-center gap-1.5">
                          <span>Display Profile in Find Care</span>
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-primary/10 text-primary border-primary/20">Find Care Search</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Show profile in patient search results</p>
                      </div>
                      <div className="flex items-center gap-2 pl-2 border-l border-border shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleViewPublicProfile}
                          className="h-7 px-2 text-xs font-semibold gap-1 text-primary hover:bg-primary/10"
                          title="Preview public profile"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </Button>
                        <Controller
                          control={control}
                          name="isVisibleInFindCare"
                          render={({ field }) => (
                            <Switch
                              checked={field.value !== false}
                              onCheckedChange={field.onChange}
                              data-testid="switch-visible-in-find-care"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Inline Photo Position & Centering Adjuster */}
                  {isPhotoAdjusterOpen && (
                    <InlinePhotoAdjuster
                      imageSrc={tempImageSrc}
                      onSave={handleCropComplete}
                      onCancel={() => setIsPhotoAdjusterOpen(false)}
                      isUploading={isPhotoUploading}
                    />
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <FieldLabel>Salutation</FieldLabel>
                      <Controller
                        control={control}
                        name="salutation"
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div><FieldLabel>First Name</FieldLabel><Input {...register("firstName")} className="mt-1" /></div>
                    <div><FieldLabel>Middle Name</FieldLabel><Input {...register("middleName")} className="mt-1" /></div>
                    <div><FieldLabel>Last Name</FieldLabel><Input {...register("lastName")} className="mt-1" /></div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <FieldLabel>Display Name</FieldLabel>
                      <Input {...register("displayName")} className="mt-1" />
                    </div>
                    <div>
                      <FieldLabel>Gender</FieldLabel>
                      <Controller
                        control={control}
                        name="gender"
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} value={field.value || ""}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-1">
                      <FieldLabel>Email</FieldLabel>
                      <Input
                        value={user?.email || ""}
                        disabled
                        className="mt-1 bg-slate-50/50 dark:bg-slate-900/50 text-slate-900 dark:text-white disabled:opacity-100 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <FieldLabel>Languages Spoken</FieldLabel>
                    <div className="mt-1">
                      <Controller
                        control={control}
                        name="languages"
                        render={({ field }) => (
                          <MultiSelect options={LANGUAGES} value={field.value} onChange={field.onChange} placeholder="Select languages..." />
                        )}
                      />
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection
                  title="Professional Bio & Headline"
                  subtitle="Public headline and background summary shown to patients"
                  icon={SettingsIcon}
                >
                  <div><FieldLabel>Professional Headline</FieldLabel><Input {...register("headline")} placeholder="e.g. Compassionate Family Medicine Physician" className="mt-1" /></div>
                  <div>
                    <FieldLabel>Professional Bio / Synopsis</FieldLabel>
                    <textarea {...register("synopsis")} className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" placeholder="Tell patients about your background, approach to care, etc." />
                  </div>
                </CollapsibleSection>

                <CollapsibleSection
                  title="Credentials & Education"
                  subtitle="Board certifications, degrees, fellowships, and education history"
                  icon={Shield}
                  headerAction={
                    <Button type="button" variant="outline" size="sm" onClick={() => addCred({ credentialType: "", title: "", institution: "", year: "" })} className="rounded-xl h-8 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> Add Credential
                    </Button>
                  }
                >
                  {credFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-12 gap-3 items-start mb-3 bg-muted/30 p-3 rounded-lg relative group">
                      <div className="col-span-12 sm:col-span-3">
                        <Controller
                          control={control}
                          name={`credentials.${index}.credentialType`}
                          render={({ field }) => (
                            <Select onValueChange={field.onChange} value={field.value || ""}>
                              <SelectTrigger className="h-10 text-sm">
                                <SelectValue placeholder="Type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {CREDENTIAL_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                      <div className="col-span-12 sm:col-span-3"><Input {...register(`credentials.${index}.title` as const)} placeholder="Title" /></div>
                      <div className="col-span-12 sm:col-span-3"><Input {...register(`credentials.${index}.institution` as const)} placeholder="Institution Name" /></div>
                      <div className="col-span-10 sm:col-span-2"><Input {...register(`credentials.${index}.year` as const)} placeholder="Year" /></div>
                      <div className="col-span-2 sm:col-span-1 flex justify-end">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeCred(index)} className="h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                  {credFields.length === 0 && <p className="text-sm text-muted-foreground italic">No credentials added. Add your education and board certifications here.</p>}
                </CollapsibleSection>

                <CollapsibleSection
                  title="Practice & Provider Type Information"
                  subtitle="Facility type, NPI number, provider types, billing code types, and visit types"
                  icon={Building2}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <FieldLabel>Facility Type *</FieldLabel>
                      <Controller
                        control={control}
                        name="facilityType"
                        render={({ field }) => (
                          <Select
                            value={field.value || ""}
                            onValueChange={val => {
                              field.onChange(val);
                              setValue("practiceType", "group");
                            }}
                          >
                            <SelectTrigger className="mt-1 w-full rounded-xl">
                              <SelectValue placeholder="Select Facility Type..." />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl max-h-72">
                              {FACILITY_TYPE_LIST.map(ft => (
                                <SelectItem key={ft.name} value={ft.name} className="py-2">
                                  <div>
                                    <div className="font-bold text-xs text-foreground">{ft.name}</div>
                                    <div className="text-[11px] text-muted-foreground line-clamp-1">{ft.description}</div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <FieldLabel>Individual NPI (Type 1)</FieldLabel>
                      <Input
                        {...register("individualNpiNumber")}
                        placeholder="e.g. 1847291034"
                        className="mt-1 font-mono rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <FieldLabel>Organizational / Group NPI (Type 2)</FieldLabel>
                      <Input
                        {...register("groupNpiNumber")}
                        placeholder="e.g. 1982736450"
                        className="mt-1 font-mono rounded-xl"
                      />
                    </div>
                    <div>
                      <FieldLabel>Affiliated Practice / Organization Name</FieldLabel>
                      <Input
                        {...register("affiliatedName")}
                        placeholder="e.g. Mount Sinai Medical Center"
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  </div>

                  {watch("facilityType") && (
                    <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl text-xs space-y-1">
                      <span className="font-bold text-primary">Facility Description: </span>
                      <span className="text-muted-foreground font-medium">
                        {FACILITY_TYPE_LIST.find(ft => ft.name === watch("facilityType"))?.description}
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <FieldLabel>Provider Type</FieldLabel>
                      <div className="mt-1">
                        <Controller control={control} name="providerTypes" render={({ field }) => (
                          <MultiSelect options={PROVIDER_TYPES} value={field.value} onChange={field.onChange} placeholder="Select provider types..." />
                        )} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel>Billing Code Types</FieldLabel>
                      <div className="mt-1">
                        <Controller control={control} name="codeTypes" render={({ field }) => (
                          <MultiSelect
                            options={CODE_TYPE_OPTIONS}
                            value={field.value || []}
                            onChange={(newVal: string[]) => {
                              field.onChange(newVal);
                              setCategoryRates(prev => {
                                const updated = { ...prev };
                                newVal.forEach(ct => {
                                  const match = activeCodeTypes.find((act: any) => act.id === ct || act.name === ct);
                                  const ctName = match?.name || ct;
                                  if (!isUUID(ctName) && updated[ctName] === undefined) {
                                    updated[ctName] = 100;
                                  }
                                  if (match?.id && isUUID(match.id)) {
                                    delete updated[match.id];
                                  }
                                });
                                form.setValue("categoryRates" as any, updated, { shouldDirty: true });
                                return updated;
                              });
                            }}
                            placeholder="Select billing code types..."
                          />
                        )} />
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>

                <CollapsibleSection
                  title="Online Presence & External Links"
                  subtitle="Configure your directory profile links and social media accounts"
                  icon={Globe}
                >
                  <SocialLinksEditor control={control} />
                </CollapsibleSection>
              </motion.div>
            </TabsContent>

            <TabsContent value="locations">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-card p-4 sm:p-5 rounded-2xl border border-border shadow-xs">
                  <div>
                    <h3 className="font-bold text-foreground text-base">Practice Locations ({locFields.length})</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Manage addresses, phone numbers, localities, and operating hours</p>
                  </div>
                  <div className="flex items-center gap-1 bg-muted p-1 rounded-xl border border-border shrink-0 self-start sm:self-auto">
                    <button
                      type="button"
                      onClick={() => setLocationsViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${locationsViewMode === 'list'
                        ? 'bg-card text-indigo-600 dark:text-indigo-400 shadow-xs'
                        : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      <List className="w-3.5 h-3.5" />
                      List View
                    </button>
                    <button
                      type="button"
                      onClick={() => setLocationsViewMode('grid')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${locationsViewMode === 'grid'
                        ? 'bg-card text-indigo-600 dark:text-indigo-400 shadow-xs'
                        : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                      Grid View
                    </button>
                  </div>
                </div>

                <div className={locationsViewMode === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 gap-6" : "space-y-6"}>
                  {locFields.map((loc, index) => {
                    const locAddress = watch(`locations.${index}.address1` as const) || `Location ${index + 1}`;
                    const locCity = watch(`locations.${index}.city` as const);
                    const locState = watch(`locations.${index}.state` as const);
                    const subtitleStr = locCity || locState ? `${locCity}${locCity && locState ? ', ' : ''}${locState}` : "Address & Office Hours";

                    return (
                      <CollapsibleSection
                        key={loc.id}
                        title={`Location ${index + 1}${locAddress && locAddress !== `Location ${index + 1}` ? `: ${locAddress}` : ''}`}
                        subtitle={subtitleStr}
                        icon={MapPin}
                        badgeText={undefined}
                        headerAction={
                          <div className="flex items-center flex-wrap gap-1.5 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                            {locFields.length > 1 && (
                              <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  disabled={index === 0}
                                  onClick={() => {
                                    moveLoc(index, index - 1);
                                    const locs = watch("locations");
                                    locs.forEach((_, i) => {
                                      setValue(`locations.${i}.isPrimary` as const, i === 0, { shouldDirty: true });
                                    });
                                  }}
                                  className="w-7 h-7 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30"
                                  title="Move Location Up"
                                  data-testid={`button-move-up-location-${index}`}
                                >
                                  <ArrowUp className="w-3.5 h-3.5" />
                                </Button>

                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  disabled={index === locFields.length - 1}
                                  onClick={() => {
                                    moveLoc(index, index + 1);
                                    const locs = watch("locations");
                                    locs.forEach((_, i) => {
                                      setValue(`locations.${i}.isPrimary` as const, i === 0, { shouldDirty: true });
                                    });
                                  }}
                                  className="w-7 h-7 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30"
                                  title="Move Location Down"
                                  data-testid={`button-move-down-location-${index}`}
                                >
                                  <ArrowDown className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                            <Controller
                              control={control}
                              name={`locations.${index}.isActive` as const}
                              defaultValue={true}
                              render={({ field }) => (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => field.onChange(field.value === false ? true : false)}
                                  className={cn(
                                    "h-8 px-2.5 text-xs font-semibold rounded-full border transition-all",
                                    field.value !== false
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                      : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                                  )}
                                >
                                  <span className={cn("w-1.5 h-1.5 rounded-full mr-1.5", field.value !== false ? "bg-emerald-500" : "bg-amber-500")} />
                                  {field.value !== false ? "Active" : "Inactive "}
                                </Button>
                              )}
                            />
                            {index > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeLoc(index)}
                                className="text-destructive hover:bg-destructive/10 h-8 px-2 text-xs"
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                              </Button>
                            )}
                          </div>
                        }
                      >
                        <div className="grid sm:grid-cols-3 gap-4 mb-4">
                          <div>
                            <FieldLabel>Building No.</FieldLabel>
                            <Input {...register(`locations.${index}.buildingNo` as const)} placeholder="Bldg A, Suite 400" className="mt-1" />
                          </div>
                          <div className="sm:col-span-2">
                            <FieldLabel required>Address Line 1</FieldLabel>
                            <Input {...register(`locations.${index}.address1` as const, { required: true })} placeholder="123 Main St" className="mt-1" />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          <div><FieldLabel>Address Line 2 (Optional)</FieldLabel><Input {...register(`locations.${index}.address2` as const)} placeholder="Suite 100" className="mt-1" /></div>
                          <div><FieldLabel required>City</FieldLabel><Input {...register(`locations.${index}.city` as const, { required: true })} className="mt-1" /></div>
                          <div>
                            <FieldLabel required>State</FieldLabel>
                            <Controller
                              control={control}
                              name={`locations.${index}.state`}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <Select onValueChange={field.onChange} value={field.value || ""}>
                                  <SelectTrigger className="mt-1 h-10">
                                    <SelectValue placeholder="Select State" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </div>

                          <div><FieldLabel required>Zip Code</FieldLabel><Input {...register(`locations.${index}.zipCode` as const, { required: true })} className="mt-1" /></div>

                          <div className="md:col-span-2">
                            <FieldLabel required>
                              Locality / Area (Location #{index + 1})
                            </FieldLabel>
                            <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
                              {watch(`locations.${index}.state` as const) ? `Showing localities for ${watch(`locations.${index}.state` as const)}` : "Select state above to filter localities"}
                            </p>
                            <Controller
                              control={control}
                              name={`locations.${index}.localityIds` as const}
                              rules={{ required: true }}
                              render={({ field }) => {
                                const locState = watch(`locations.${index}.state` as const);
                                const locValueArr = watch(`locations.${index}.localityIds` as const) || field.value || [];
                                const currentLocId = Array.isArray(locValueArr) && locValueArr.length > 0 ? String(locValueArr[0]) : (typeof locValueArr === "string" ? locValueArr : "");

                                const formatLocLabel = (loc: any) => {
                                  const codeVal = loc.localityNumber || loc.code || loc.state;
                                  return `${loc.name}${codeVal ? ` (${codeVal})` : ''}`;
                                };

                                const stateLocalities = locState
                                  ? masterLocalitiesData.filter((loc: any) => loc.state === locState).map((loc: any) => ({
                                    value: loc.id.toString(),
                                    label: formatLocLabel(loc)
                                  }))
                                  : (masterLocalitiesData.length > 0 ? masterLocalitiesData : MASTER_LOCALITIES).map((loc: any) => ({
                                    value: (loc.id || loc.value || "").toString(),
                                    label: loc.label || formatLocLabel(loc)
                                  }));

                                return (
                                  <SingleLocalitySelect
                                    value={currentLocId}
                                    placeholder={locState ? `Select locality in ${locState}...` : "Select locality..."}
                                    options={stateLocalities}
                                    onChange={(val: string) => {
                                      const newArr = val ? [val] : [];
                                      field.onChange(newArr);
                                      setValue(`locations.${index}.localityIds` as const, newArr, { shouldDirty: true });
                                    }}
                                  />
                                );
                              }}
                            />
                          </div>

                          <div><FieldLabel>Location Phone</FieldLabel><Input {...register(`locations.${index}.phone` as const)} className="mt-1" /></div>
                          <div><FieldLabel>Office Phone</FieldLabel><Input {...register(`locations.${index}.officePhone` as const)} className="mt-1" /></div>
                          <div><FieldLabel>Fax</FieldLabel><Input {...register(`locations.${index}.fax` as const)} className="mt-1" /></div>
                          <div><FieldLabel>Email</FieldLabel><Input {...register(`locations.${index}.email` as const)} className="mt-1" /></div>
                        </div>

                        <div className="mt-4 border-t pt-4">
                          <FieldLabel>Office Hours</FieldLabel>
                          <div className={cn("grid gap-3 sm:gap-4 mt-3", locationsViewMode === 'grid' ? "grid-cols-1 2xl:grid-cols-2" : "grid-cols-1 xl:grid-cols-2")}>
                            {loc.schedules.map((schedule, dayIndex) => (
                              <div key={schedule.dayOfWeek} className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 bg-muted/20 p-2.5 sm:p-3 rounded-xl border">
                                <div className="flex items-center justify-between gap-2 sm:gap-3 w-full sm:w-auto">
                                  <div className="w-16 sm:w-20 font-medium text-xs sm:text-sm truncate">{schedule.dayOfWeek}</div>
                                  <Controller
                                    control={control}
                                    name={`locations.${index}.schedules.${dayIndex}.isClosed` as const}
                                    render={({ field }) => (
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        <Switch checked={field.value} onCheckedChange={field.onChange} className="scale-75 sm:scale-90 transform origin-left" />
                                        <Label className="text-[10px] sm:text-xs text-muted-foreground w-8 sm:w-9">{field.value ? "Closed" : "Open"}</Label>
                                      </div>
                                    )}
                                  />
                                </div>
                                {!watch(`locations.${index}.schedules.${dayIndex}.isClosed` as const) && (
                                  <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 ml-auto sm:ml-0">
                                    <Controller
                                      control={control}
                                      name={`locations.${index}.schedules.${dayIndex}.startTime`}
                                      render={({ field }) => (
                                        <Select onValueChange={field.onChange} value={field.value || ""}>
                                          <SelectTrigger className="h-7 sm:h-8 text-[10px] sm:text-xs w-[75px] sm:w-[85px] px-1.5 sm:px-2">
                                            <SelectValue placeholder="Time" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    />
                                    <span className="text-[10px] sm:text-xs text-muted-foreground">to</span>
                                    <Controller
                                      control={control}
                                      name={`locations.${index}.schedules.${dayIndex}.endTime`}
                                      render={({ field }) => (
                                        <Select onValueChange={field.onChange} value={field.value || ""}>
                                          <SelectTrigger className="h-7 sm:h-8 text-[10px] sm:text-xs w-[75px] sm:w-[85px] px-1.5 sm:px-2">
                                            <SelectValue placeholder="Time" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Associated Team Providers for this Practice Location */}
                        <div className="mt-6 border-t pt-4">
                          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <div>
                              <FieldLabel>
                                <span className="flex items-center gap-2 font-bold text-slate-900 dark:text-white">
                                  <Users className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Associated Team Providers
                                </span>
                              </FieldLabel>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                                Select which team members from My Team work at Location #{index + 1}.
                              </p>
                            </div>
                            {teamInvitations?.data && teamInvitations.data.length > 0 && (
                              <Badge variant="outline" className="text-[11px] font-bold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800">
                                {teamInvitations.data.filter((inv: any) => {
                                  const currentLocList = watch("locations") || locFields || [];
                                  const locIdStr = String((loc as any).id || (currentLocList[index] as any)?.id || `loc_${index + 1}`);
                                  const invRates = inv.categoryRates || {};
                                  const assignedIds: string[] = invRates.assignedLocationIds || inv.assignedLocationIds || [];
                                  return assignedIds.length === 0 || assignedIds.includes(locIdStr) || assignedIds.includes(`loc_${index + 1}`);
                                }).length} / {teamInvitations.data.length} Members Assigned
                              </Badge>
                            )}
                          </div>

                          {teamInvitationsLoading ? (
                            <div className="text-xs text-slate-500 p-3 border rounded-xl bg-slate-50 dark:bg-slate-800/40">Loading team members...</div>
                          ) : teamInvitations?.data && teamInvitations.data.length > 0 ? (
                            <div className={cn("grid gap-3 mt-3", locationsViewMode === 'grid' ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-1 sm:grid-cols-2")}>
                              {teamInvitations.data.map((inv: any) => {
                                const currentLocList = watch("locations") || locFields || [];
                                const locIdStr = String((loc as any).id || (currentLocList[index] as any)?.id || `loc_${index + 1}`);
                                const locIndexStr = `loc_${index + 1}`;
                                const invRates = (inv.categoryRates as any) || {};
                                const assignedIds: string[] = invRates.assignedLocationIds || inv.assignedLocationIds || [];
                                
                                const isAssigned = assignedIds.length === 0 || assignedIds.includes(locIdStr) || assignedIds.includes(locIndexStr);
                                const providerName = inv.email ? inv.email.split('@')[0] : 'Team Provider';
                                const feeScheduleSummary = inv.categoryRates && Object.keys(inv.categoryRates).filter((k: string) => k !== 'assignedLocationIds' && k !== 'assignedLocations' && !k.startsWith('loc_')).length > 0
                                  ? 'Custom Fee Schedule Rates'
                                  : `${inv.percentage ?? (parseFloat(watch("medicareCharge") || "50") || 50)}% Fee Schedule (Facility Default)`;

                                return (
                                  <div
                                    key={inv.id}
                                    className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition-all ${
                                      isAssigned
                                        ? 'bg-indigo-50/50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800'
                                        : 'bg-slate-50 dark:bg-slate-800/20 border-slate-200 dark:border-slate-800 opacity-60'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <Checkbox
                                        id={`loc-${index}-team-${inv.id}`}
                                        checked={isAssigned}
                                        onCheckedChange={(checked) => {
                                          const shouldBeAssigned = Boolean(checked);
                                          const allLocList = watch("locations") || locFields || [];
                                          const allLocIds = allLocList.map((l: any, i: number) => String(l.id || `loc_${i + 1}`));
                                          
                                          let newAssignedIds: string[] = [];
                                          if (assignedIds.length === 0) {
                                            newAssignedIds = allLocIds.filter((id: string) => id !== locIdStr && id !== locIndexStr);
                                          } else {
                                            if (shouldBeAssigned) {
                                              newAssignedIds = Array.from(new Set([...assignedIds, locIdStr, locIndexStr]));
                                            } else {
                                              newAssignedIds = assignedIds.filter(id => id !== locIdStr && id !== locIndexStr);
                                            }
                                          }

                                          const selectedLocNames = allLocList
                                            .filter((l: any, i: number) => {
                                              const idStr = String(l.id || `loc_${i + 1}`);
                                              return newAssignedIds.includes(idStr) || newAssignedIds.includes(`loc_${i + 1}`);
                                            })
                                            .map((l: any) => l.address1 || l.city || `Location`);

                                          const updatedCategoryRates = {
                                            ...(invRates || {}),
                                            assignedLocationIds: newAssignedIds,
                                            assignedLocations: selectedLocNames
                                          };

                                          updateInvitationMutation.mutate(
                                            {
                                              id: inv.id,
                                              categoryRates: updatedCategoryRates
                                            },
                                            {
                                              onSuccess: () => {
                                                toast({
                                                  title: "Location Assignment Updated",
                                                  description: `${inv.email} is now ${shouldBeAssigned ? 'assigned to' : 'removed from'} Location #${index + 1}.`
                                                });
                                                refetchTeamInvitations();
                                              },
                                              onError: (err: any) => {
                                                toast({
                                                  title: "Update Failed",
                                                  description: err.message || "Failed to update location assignment.",
                                                  variant: "destructive"
                                                });
                                              }
                                            }
                                          );
                                        }}
                                      />
                                      <div className="min-w-0">
                                        <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{providerName}</p>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{inv.email} • NPI: {inv.npiNumber}</p>
                                        <span className="inline-block text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 mt-0.5">{feeScheduleSummary}</span>
                                      </div>
                                    </div>
                                    <Badge variant={isAssigned ? "default" : "outline"} className={`text-[10px] shrink-0 font-bold ${isAssigned ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}>
                                      {isAssigned ? "Assigned" : "Unassigned"}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="p-4 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl text-center bg-slate-50/50 dark:bg-slate-800/20 space-y-2 mt-2">
                              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">No team members added to your group practice yet.</p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/provider/group-team')}
                                className="text-xs font-semibold rounded-lg gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" /> Invite Team Members in My Team
                              </Button>
                            </div>
                          )}
                        </div>
                      </CollapsibleSection>
                    );
                  })}
                </div>

                <div className="flex gap-3 justify-end mt-4">
                  <Button type="button" variant="outline" onClick={() => addLoc({ isPrimary: false, buildingNo: "", address1: "", address2: "", city: "", state: "", zipCode: "", phone: "", officePhone: "", fax: "", email: "", website: "", localityIds: [], schedules: [...DEFAULT_SCHEDULES] })} className="rounded-xl">
                    <Plus className="w-4 h-4 mr-2" /> Add Location
                  </Button>
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="rates">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <CollapsibleSection
                  title="Fee Schedule Rates by Billing Code Type"
                  subtitle="Configure Fee Schedule percentage rates (% of Fee Schedule) globally or per practice location."
                  icon={Percent}
                  headerAction={
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setIsAddCodeTypeModalOpen(true)}
                      className="gap-1.5 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs"
                    >
                      <Plus className="w-4 h-4" />
                      Add Billing Code Type
                    </Button>
                  }
                >
                  {/* Practice Location Selector Card */}
                  <div className="bg-muted/30 p-4 sm:p-5 rounded-2xl border border-border space-y-3.5">
                    <div>
                      <FieldLabel>Select Practice Location to Configure Rates</FieldLabel>
                    </div>

                    <div className="flex items-center gap-2 overflow-x-auto pb-2 pt-0.5 scrollbar-none w-full">
                      <button
                        type="button"
                        onClick={() => setSelectedRateLocationIndex(-1)}
                        className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 shrink-0 border ${
                          selectedRateLocationIndex === -1
                            ? "bg-primary text-primary-foreground border-primary shadow-xs"
                            : "bg-card hover:bg-muted text-foreground border-border shadow-2xs"
                        }`}
                      >
                        <Globe className="w-4 h-4 shrink-0" />
                        <span>All Locations (Global Default)</span>
                      </button>

                      {locFields.map((locItem: any, idx: number) => {
                        const isSelected = selectedRateLocationIndex === idx;
                        const label = locItem.address1 || locItem.street1 || `Location ${idx + 1}`;
                        const cityState = [locItem.city, locItem.state].filter(Boolean).join(", ");
                        const keys = getLocationKeys(locItem, idx);
                        const hasCustomOverrides = keys.some(k => categoryRates[k] && typeof categoryRates[k] === "object" && Object.keys(categoryRates[k]).length > 0);

                        return (
                          <button
                            key={locItem.id || idx}
                            type="button"
                            onClick={() => setSelectedRateLocationIndex(idx)}
                            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 shrink-0 border ${
                              isSelected
                                ? "bg-primary text-primary-foreground border-primary shadow-xs"
                                : "bg-card hover:bg-muted text-foreground border-border shadow-2xs"
                            }`}
                          >
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            <span>Location {idx + 1}: {label} {cityState ? `(${cityState})` : ''}</span>
                            {hasCustomOverrides && (
                              <span className={`w-2 h-2 rounded-full shrink-0 ${isSelected ? "bg-amber-300 animate-pulse" : "bg-emerald-500"}`} title="Custom location overrides configured" />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {selectedRateLocationIndex !== -1 && (
                      <div className="bg-amber-500/10 border border-amber-500/20 text-amber-900 dark:text-amber-200 p-3 sm:p-3.5 rounded-xl text-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <span className="flex items-center gap-2 font-medium leading-relaxed">
                          <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                          <span>Configuring location-wise rate for <strong>Location {selectedRateLocationIndex + 1} ({locFields[selectedRateLocationIndex]?.address1 || 'Practice Location'})</strong>. Values set here override the global baseline.</span>
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-3 text-xs font-bold text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 shrink-0 self-end sm:self-auto rounded-lg"
                          onClick={() => handleResetLocationRatesToDefault(selectedRateLocationIndex)}
                        >
                          Reset Overrides
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Unified Billing Code Types Selection Card */}
                  <div className="bg-muted/30 p-4 sm:p-5 rounded-2xl border border-border space-y-3">
                    <div className="space-y-1">
                      <FieldLabel>Select Billing Code Types</FieldLabel>
                      <p className="text-xs text-muted-foreground leading-relaxed">Select the billing code types used in your practice to configure their Fee Schedule percentage rate (%) below.</p>
                    </div>
                    <Controller
                      control={control}
                      name="codeTypes"
                      render={({ field }) => (
                        <MultiSelect
                          options={CODE_TYPE_OPTIONS}
                          value={field.value || []}
                          onChange={(newVal: string[]) => {
                            field.onChange(newVal);
                            setCategoryRates(prev => {
                              const updated = { ...prev };
                              newVal.forEach(ct => {
                                if (updated[ct] === undefined) {
                                  updated[ct] = 100;
                                }
                              });
                              form.setValue("categoryRates" as any, updated, { shouldDirty: true });
                              return updated;
                            });
                          }}
                          placeholder="Select billing code types..."
                        />
                      )}
                    />
                  </div>

                  {/* Rate Cards Grid */}
                  {displayedCodeTypes.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                      {displayedCodeTypes.map((codeTypeObj: any, idx: number) => {
                        const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                          ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id || idx))
                          : String(codeTypeObj || `CodeType-${idx}`);
                        const codeTypeId = codeTypeObj?.id || codeTypeName;
                        const { rate: currentRate, isCustom } = getEffectiveRateForCodeType(codeTypeName, codeTypeId);

                        return (
                          <div
                            key={codeTypeId}
                            className="bg-card hover:border-primary/40 transition-all rounded-2xl border border-border/80 p-4 sm:p-5 flex flex-col justify-between shadow-xs hover:shadow-sm space-y-4"
                          >
                            <div className="flex items-start justify-between gap-2.5 pb-1">
                              <div className="space-y-1.5 min-w-0 flex-1">
                                <div className="text-sm sm:text-base font-bold text-foreground leading-snug">
                                  {String(codeTypeName)}
                                </div>
                                <div>
                                  <Badge 
                                    variant="outline" 
                                    className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold inline-flex ${
                                      isCustom 
                                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" 
                                        : "bg-primary/10 text-primary border-primary/20"
                                    }`}
                                  >
                                    {selectedRateLocationIndex === -1 ? "Global Rate %" : isCustom ? "Custom Loc Rate %" : "Inherited %"}
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors shrink-0"
                                onClick={() => handleRemoveBillingCodeType(codeTypeName, codeTypeId)}
                                title="Remove Billing Code Type"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="space-y-1.5">
                              <div className="relative">
                                <Input
                                  type="number"
                                  min={1}
                                  value={currentRate}
                                  onChange={(e) => {
                                    const val = Number(e.target.value);
                                    handleUpdateCodeTypeRate(codeTypeName, codeTypeId, val);
                                  }}
                                  className="pr-9 h-11 text-base font-bold font-mono bg-background border-border rounded-xl focus:bg-background transition-all"
                                />
                                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">%</span>
                              </div>
                            </div>
                            <div className="pt-2.5 border-t border-border/40">
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {selectedRateLocationIndex === -1 
                                  ? `Applies to all ${codeTypeName} CPT codes across all practice locations as default baseline.` 
                                  : `Applies to ${codeTypeName} CPT codes for Location ${selectedRateLocationIndex + 1}.`
                                }
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 px-6 border-2 border-dashed border-border/80 rounded-2xl bg-muted/20 space-y-3">
                      <div className="w-12 h-12 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto text-muted-foreground/60 mb-1">
                        <Percent className="w-6 h-6" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-foreground">No Billing Code Types added yet</p>
                        <p className="text-xs text-muted-foreground">Click below to add a Billing Code Type and set its percentage rate.</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setIsAddCodeTypeModalOpen(true)}
                        className="gap-1.5 text-xs font-semibold rounded-xl mt-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Billing Code Type
                      </Button>
                    </div>
                  )}
                </CollapsibleSection>
              </motion.div>
            </TabsContent>

            {/* <TabsContent value="notifications">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                <CollapsibleSection
                  title="Notification Preferences"
                  subtitle="Configure how you receive appointment confirmations, GFE updates, and patient alerts"
                  icon={Bell}
                >
                  {[
                    { label: "Email Notifications", desc: "Appointment confirmations, GFE updates", value: emailNotif, onChange: setEmailNotif, id: "email" },
                    { label: "SMS Alerts", desc: "Urgent patient messages", value: smsNotif, onChange: setSmsNotif, id: "sms" },
                    { label: "Patient Messages", desc: "In-platform patient communications", value: patientMessages, onChange: setPatientMessages, id: "messages" },
                  ].map(item => (
                    <div key={item.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                      <div>
                        <p className="font-medium text-sm">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch checked={item.value} onCheckedChange={item.onChange} data-testid={`switch-${item.id}`} />
                    </div>
                  ))}
                </CollapsibleSection>
              </motion.div>
            </TabsContent> */}

            <TabsContent value="appearance">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                <CollapsibleSection
                  title="Theme & Interface Customization"
                  subtitle="Choose between light and dark theme mode for your dashboard"
                  icon={Palette}
                >
                  <div>
                    <Label className="mb-3 block">Theme Mode</Label>
                    <div className="grid grid-cols-2 max-w-xs sm:max-w-sm gap-3">
                      {[
                        { value: "light", icon: Sun, label: "Light" },
                        { value: "dark", icon: Moon, label: "Dark" },
                      ].map(t => (
                        <button
                          key={t.value}
                          onClick={() => setTheme(t.value)}
                          type="button"
                          className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${theme === t.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                          data-testid={`button-theme-${t.value}`}
                        >
                          <t.icon className={`w-5 h-5 ${theme === t.value ? "text-primary" : "text-muted-foreground"}`} />
                          <span className={`text-sm font-medium ${theme === t.value ? "text-primary" : "text-muted-foreground"}`}>{t.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </CollapsibleSection>
              </motion.div>
            </TabsContent>
          </Tabs>
        </form>
      </div>

      <ConfirmDialog
        open={!!pendingNav}
        onOpenChange={(open) => !open && setPendingNav(null)}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to discard them and leave this page?"
        confirmText="Discard Changes"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={() => {
          if (pendingNav) {
            reset();
            setTimeout(() => {
              navigate(pendingNav);
            }, 0);
          }
        }}
      />

      {/* Add Billing Code Type Dialog */}
      <Dialog open={isAddCodeTypeModalOpen} onOpenChange={setIsAddCodeTypeModalOpen}>
        <DialogContent className="w-[92vw] max-w-md rounded-2xl p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Plus className="w-5 h-5 text-primary" /> Add Billing Code Type
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Add a new Billing Code Type and set its Fee Schedule percentage rate (%).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Select or Enter Billing Code Type</Label>
              <Select
                value={newCodeTypeName}
                onValueChange={(val) => {
                  setNewCodeTypeName(val);
                  if (val !== "OTHER_CUSTOM") {
                    setCustomCodeTypeName("");
                  }
                }}
              >
                <SelectTrigger className="h-10 text-sm">
                  <SelectValue placeholder="Select Billing Code Type..." />
                </SelectTrigger>
                <SelectContent>
                  {activeCodeTypes.map((ct: any) => (
                    <SelectItem key={ct.id || ct.name} value={ct.name}>
                      {ct.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="OTHER_CUSTOM">+ Add Custom Code Type...</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newCodeTypeName === "OTHER_CUSTOM" && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Custom Code Type Name</Label>
                <Input
                  type="text"
                  placeholder="e.g., Radiology, Pathology, DME"
                  value={customCodeTypeName}
                  onChange={(e) => setCustomCodeTypeName(e.target.value)}
                  className="h-10 text-sm"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-semibold">Fee Schedule Percentage Rate (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  min={1}
                  max={500}
                  placeholder="100"
                  value={newCodeTypeRate}
                  onChange={(e) => setNewCodeTypeRate(e.target.value === "" ? "" : Number(e.target.value))}
                  className="h-10 text-sm pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">%</span>
              </div>
              <p className="text-[11px] text-muted-foreground">Base percentage of CMS Fee Schedule to charge for this billing code type.</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" type="button" onClick={() => setIsAddCodeTypeModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddBillingCodeType}
              disabled={!newCodeTypeName || (newCodeTypeName === "OTHER_CUSTOM" && !customCodeTypeName.trim())}
            >
              Add & Save Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
